import { trackFacebookEvent } from "@/lib/facebook-pixel"

type DataLayerEvent = Record<string, unknown> & {
  event: string
}

export const GOOGLE_SIGNUP_PENDING_STORAGE_KEY = "google_signup_pending"

export function pushDataLayerEvent(event: DataLayerEvent): void {
  if (typeof window === "undefined") return

  const win = window as Window & { dataLayer?: DataLayerEvent[] }
  if (!Array.isArray(win.dataLayer)) {
    win.dataLayer = []
  }

  win.dataLayer.push(event)
}

export function trackSignUpClickEvent(): void {
  pushDataLayerEvent({ event: "sign_up_click" })
  // CTA intent on landing — do not fire StartTrial here.
  trackFacebookEvent("ViewContent")
}

export function trackSignUpSubmitEvent(): void {
  pushDataLayerEvent({ event: "sign_up_submit" })
}

export function trackSignUpCompleteEvent(): void {
  pushDataLayerEvent({ event: "sign_up_complete" })
}

export function trackUpgradeClickEvent(): void {
  pushDataLayerEvent({ event: "upgrade_click" })
}

type CheckoutEventOptions = {
  value?: number
  currency?: string
  context?: string
  planId?: string
  billingInterval?: "monthly" | "yearly"
}

/** Account created: StartTrial → AddToCart. Checkout/Purchase fire later in onboarding. */
export function trackAccountCreatedFunnelEvents(options: CheckoutEventOptions = {}): void {
  const value = Number.isFinite(options.value) ? Number(options.value) : 0
  const currency = options.currency || "USD"
  trackStartTrialEvent(value, currency)
  trackAddToCartEvent(options)
}

export function trackInitiateCheckoutEvent(options: CheckoutEventOptions = {}): void {
  const normalizedValue = Number.isFinite(options.value)
    ? Number((options.value ?? 0).toFixed(2))
    : undefined
  const currency = options.currency || "USD"

  const payload: Record<string, unknown> = {
    currency,
  }

  if (typeof normalizedValue === "number") {
    payload.value = normalizedValue
  }
  if (options.context) {
    payload.context = options.context
  }
  if (options.planId) {
    payload.plan_id = options.planId
  }
  if (options.billingInterval) {
    payload.billing_interval = options.billingInterval
  }

  pushDataLayerEvent({
    event: "initiate_checkout",
    ...payload,
  })
  trackFacebookEvent("InitiateCheckout", payload)
}

export function trackAddToCartEvent(options: CheckoutEventOptions = {}): void {
  const normalizedValue = Number.isFinite(options.value)
    ? Number((options.value ?? 0).toFixed(2))
    : undefined
  const currency = options.currency || "USD"

  const payload: Record<string, unknown> = {
    currency,
  }

  if (typeof normalizedValue === "number") {
    payload.value = normalizedValue
  }
  if (options.context) {
    payload.context = options.context
  }
  if (options.planId) {
    payload.plan_id = options.planId
  }
  if (options.billingInterval) {
    payload.billing_interval = options.billingInterval
  }

  pushDataLayerEvent({
    event: "add_to_cart",
    ...payload,
  })
  trackFacebookEvent("AddToCart", payload)
}

export function trackPurchaseEvent(value: number, currency = "USD"): void {
  const normalizedValue = Number.isFinite(value) ? Number(value.toFixed(2)) : 0
  const payload = {
    value: normalizedValue,
    currency,
  }

  pushDataLayerEvent({ event: "purchase", ...payload })
  trackFacebookEvent("Purchase", payload)
}

export function trackStartTrialEvent(value: number, currency = "USD"): void {
  const normalizedValue = Number.isFinite(value) ? Number(value.toFixed(2)) : 0
  const payload = {
    value: normalizedValue,
    currency,
  }

  pushDataLayerEvent({ event: "start_trial", ...payload })
  trackFacebookEvent("StartTrial", payload)
}
